import { useState } from "react";
import { useFullscreen } from "@/hooks/useFullscreen";
import { useUIStore } from "@/stores/uiStore";

import { TopStrip } from "./topstrip/TopStrip";
import { ChartArea } from "./ChartArea";
import { StatusBar } from "./StatusBar";
import { SettingsPanel } from "@/components/features/SettingsPanel";
import { CommandPalette } from "@/components/features/CommandPalette";
import { SymbolPickerOverlay } from "@/components/lytick/SymbolPickerOverlay";

export function WorkspaceShell() {
  const settingsOpen = useUIStore((state) => state.isSettingsOpen);
  const closeSettings = useUIStore((state) => state.closeSettings);
  const activeModal = useUIStore((state) => state.activeModal);

  const { isFullscreen } = useFullscreen();
  const [headerHovered, setHeaderHovered] = useState(false);

  const headerVisible = !isFullscreen || headerHovered;

  return (
    <div
      className="workspace-shell relative overflow-hidden"
      style={{ width: "100vw", height: "100dvh" }}
    >
      {/* Chart canvas — inset below top strip */}
      <div style={{
        position: "absolute",
        top: isFullscreen ? 0 : "var(--height-topbar-desktop)",
        left: 0,
        right: 0,
        bottom: isFullscreen ? 0 : "var(--height-status)",
        zIndex: "var(--z-chart)",
      }}>
        <ChartArea />
      </div>

      {/* Fullscreen hover zone — 36px trigger at top edge */}
      {isFullscreen && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 36,
            zIndex: "var(--z-chart)",
          }}
          onMouseEnter={() => setHeaderHovered(true)}
          onMouseLeave={() => setHeaderHovered(false)}
        />
      )}

      {/* Top strip — 48px nav bar */}
      <div
        onMouseEnter={() => isFullscreen && setHeaderHovered(true)}
        onMouseLeave={() => isFullscreen && setHeaderHovered(false)}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "var(--height-topbar-desktop)",
          backgroundColor: "color-mix(in srgb, var(--bg-surface) 85%, transparent)",
          backdropFilter: "blur(12px)",
          WebkitBackdropFilter: "blur(12px)",
          borderBottom: "1px solid var(--lyt-border-subtle)",
          boxShadow: "var(--shadow-separator)",
          zIndex: "var(--z-toolbar)",
          opacity: headerVisible ? 1 : 0,
          pointerEvents: headerVisible ? undefined : "none",
          transition: "opacity var(--duration-default) ease",
        }}
      >
        <TopStrip />
      </div>

      {/* Settings panel — floating right slide-in */}
      <SettingsPanel open={settingsOpen} onClose={closeSettings} />

      {/* Status bar — hidden in fullscreen */}
      {!isFullscreen && <StatusBar />}

      <CommandPalette />

      {/* Symbol picker — CSV files list */}
      <SymbolPickerOverlay open={activeModal === "symbolSearch"} />
    </div>
  );
}
